<!-- @format -->

<template>
    <div
        class="gst-add-hotel-reservation-hotel-list-card d-flex flex-column px-2 py-4 pa-md-0"
        :class="{
            'gst-add-hotel-reservation-hotel-list-card--highlighted':
                isHighlighted && $vuetify.breakpoint.mdAndUp,
            'mt-2 mt-md-0': promotedCardChipFullWidth,
        }"
        :style="topPromotedBorderStyle"
        tabindex="0"
        @click="openItemDetails()"
        @keypress.enter="openItemDetails()"
        @mouseenter="onItemMouseEnterDo()"
        @mouseleave="onItemMouseLeaveDo()">
        <PromotedCardChip
            v-if="campaign.type"
            :background-color="campaign.config.backgroundColor"
            :font-color="campaign.config.fontColor"
            :icon-url="campaign.config.iconUrl"
            :full-width="promotedCardChipFullWidth" />
        <div
            class="d-flex flex-row"
            :class="promotedCardChipFullWidth ? 'pt-6' : ''"
            :style="{ flex: 1 }">
            <div
                :style="{ width: $vuetify.breakpoint.mdAndUp ? '100px' : '80px' }"
                class="align-self-stretch u-position-relative">
                <BaseImage
                    :src="image.src"
                    :fallback-src="image.fallBack"
                    class="u-position-absolute"
                    gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
                    height="100%"
                    width="100%" />
            </div>

            <div class="py-md-4 pl-2 px-md-2 flex-grow-1">
                <div class="gst-add-hotel-reservation-hotel-list-card__rating-wrapper">
                    <StarRating
                        :key="stars"
                        :value="stars"
                        class="gst-add-hotel-reservation-hotel-list-card__rating" />
                    <VClamp
                        v-if="!isCityCentric || isManageBookingFlow"
                        autoresize
                        :max-lines="2"
                        :title="distance"
                        tag="span"
                        class="gst-add-hotel-reservation-hotel-list-card__distance pl-1">
                        • {{ distance }}
                    </VClamp>
                </div>
                <VClamp
                    autoresize
                    :max-lines="1"
                    :title="item.name"
                    tag="h6"
                    class="gst-add-hotel-reservation-hotel-list-card__room u-width-100">
                    {{ item.name }}
                </VClamp>
                <div
                    v-if="showGiftCard || showDiscountAmount"
                    class="gst-add-hotel-reservation-hotel-list-card__gifts d-flex flex-wrap mt-2">
                    <SaveAmountCardChip
                        v-if="showDiscountAmount"
                        :amount="savings"
                        :currency="currency" />
                    <GiftCardChip
                        v-if="showGiftCard"
                        :amount="maxGiftCard ? maxGiftCard.amount : giftCard.amount"
                        :currency="maxGiftCard ? maxGiftCard.currency : giftCard.currency"
                        :up-to-label="!!maxGiftCard" />
                </div>
                <PriceDetail
                    class="mt-2"
                    :all-inclusive-pricing="allInclusivePricing"
                    :total-prices="bundleProductsTotals"
                    :currency="currency"
                    :price-per-night="pricePerNight"
                    :points="item.redeemPoints"
                    :points-per-night="item.redeemPointsPerNight" />
                <LoyaltyInfoChip
                    :show-min-max="!userIsAuth"
                    :points-info="pointsInfo"
                    :lowest-tier-status="
                        $t('shared:components.bonusPoints.loyaltyInfoChip.regularMember')
                    "
                    :highest-tier-status="
                        $t('shared:components.bonusPoints.loyaltyInfoChip.memberWithStatus')
                    " />
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import VClamp from 'vue-clamp';
import StarRating from '@core/shared/components/misc/StarRating.vue';
import { create as CreateBundleProductsModel } from '@tenant/app/modelsV2/BundleProductsModel';
import { getImageByWidth } from '@tenant/app/utils/imageModelUtils';
import { IMAGE_SIZE } from '@tenant/app/utils/constants/imageData';
import GiftCardChip from '@tenant/app/components/chips/GiftCardChip';
import SaveAmountCardChip from '@tenant/app/components/chips/SaveAmountCardChip';
import locationsConstants from '@tenant/app/utils/constants/locations';
import PriceDetail from '@tenant/app/modules/hotel/components/PriceDetail.vue';
import PromotedCardChip from '@tenant/app/components/chips/PromotedCardChip.vue';
import campaignConstants from '@tenant/app/utils/constants/campaign';
import EventFlowMixin from '@tenant/app/mixins/EventFlowMixin.js';
import LoyaltyInfoChip from '@tenants/aadvantagehotels/app/components/chips/LoyaltyInfoChip.vue';

export default {
    name: 'HotelsListCard',
    components: {
        VClamp,
        StarRating,
        GiftCardChip,
        SaveAmountCardChip,
        PromotedCardChip,
        PriceDetail,
        LoyaltyInfoChip,
    },
    mixins: [EventFlowMixin],
    props: {
        item: {
            type: Object,
            required: true,
        },
        distanceUnitOfMeasure: {
            type: String,
            required: true,
        },
        distanceFrom: {
            type: Boolean,
            default: false,
        },
        inLocation: {
            type: Object,
            required: true,
        },
        bundleProducts: {
            type: Object,
            default: function () {
                return CreateBundleProductsModel();
            },
        },
        busEventsParent: {
            type: Object,
            required: true,
        },
        isHighlighted: {
            type: Boolean,
            default: false,
        },
        distanceToVenue: {
            type: Number,
            default: 0,
        },
    },
    computed: {
        ...mapGetters({
            redeemPointsToggle: 'appState/getRedeemPointsToggle',
            userIsAuth: 'user/isAuth',
        }),
        topPromotedBorderStyle() {
            return {
                border: this.campaign.topPromoted
                    ? `1px solid ${this.campaign.config.backgroundColor}`
                    : '',
            };
        },
        promotedCardChipFullWidth() {
            return this.campaign?.type === campaignConstants.TYPE.SPONSORED;
        },
        stars() {
            const { awards } = this.item;

            if (awards.length && awards[0].rating) {
                return awards[0].rating;
            }

            return 0;
        },
        image() {
            if (this.$vuetify.breakpoint.mdAndUp) {
                return {
                    src: getImageByWidth(IMAGE_SIZE.LARGE, this.item.image),
                    fallBack: getImageByWidth(IMAGE_SIZE.ORIGINAL, this.item.image),
                };
            }
            return {
                src: getImageByWidth(IMAGE_SIZE.THUMBNAIL, this.item.image),
            };
        },
        distance() {
            if (this.distanceFrom) {
                return this.$options.filters.distanceFrom(
                    this.item.distance,
                    this.distanceUnitOfMeasure,
                    !this.isManageBookingFlow
                        ? this.$t('_common:terms.venue')
                        : this.$t('_common:terms.originalHotel')
                );
            }
            return this.$options.filters.distanceToVenue(
                this.distanceToVenue,
                this.distanceUnitOfMeasure
            );
        },
        pricePerNight() {
            return this.room.rates[0].pricePerNight;
        },
        campaign() {
            return this.item.campaign;
        },
        room() {
            return this.item.selectedRoom;
        },
        rooms() {
            return this.item.rooms;
        },
        bundleProductsTotals() {
            return this.bundleProducts.getTotals();
        },
        showBasePriceWithoutDiscount() {
            return (
                this.bundleProductsTotals.basePrice !==
                this.bundleProductsTotals.basePriceWithoutDiscount
            );
        },
        giftCard() {
            return this.room.selectedRate.giftCard;
        },
        maxGiftCard() {
            return this.item.maxGiftCard;
        },
        showGiftCard() {
            return this.giftCard?.amount || this.maxGiftCard?.amount;
        },
        showDiscountAmount() {
            return this.showBasePriceWithoutDiscount && this.bundleProductsTotals.discountedAmount;
        },
        currency() {
            return this.room.selectedRate.currency;
        },
        savings() {
            return this.bundleProductsTotals.discountedAmount;
        },
        isCityCentric() {
            return this.inLocation.type === locationsConstants.TYPES.CITY;
        },
        allInclusivePricing() {
            return this.item.allInclusivePricing;
        },
        pointsInfo() {
            return {
                minimum: !this.redeemPointsToggle
                    ? this.item.earnPointsRange.minimum
                    : this.item.redeemPointsRange.minimum,
                maximum: !this.redeemPointsToggle
                    ? this.item.earnPointsRange.maximum
                    : this.item.redeemPointsRange.maximum,
                actual: !this.redeemPointsToggle ? this.item.pointsEarned : this.item.redeemPoints,
            };
        },
    },
    methods: {
        ...mapActions({
            focusHotel: `addHotelReservationState/hotels/focusHotel`,
        }),
        openItemDetails(focusedRoom) {
            this.busEventsParent.$emit('open-item-details', this.item, focusedRoom);
        },
        onItemMouseEnterDo() {
            this.focusHotel(this.item);
        },
        onItemMouseLeaveDo() {
            this.focusHotel(null);
        },
    },
};
</script>

<style lang="scss">
@import '@scssVariables';
@import '@scssMixins';
$hoverBackgroundColor: #ebf5ff;

.gst-add-hotel-reservation-hotel-list-card {
    @include font-size('m');
    @include font-weight('regular');

    border: 1px solid theme-color-hex('senary-darken-2');
    border-radius: 3px;
    overflow: hidden;

    &:hover {
        border: 1px solid #ebf5ff;
        background-color: $hoverBackgroundColor;
    }

    &.gst-add-hotel-reservation-hotel-list-card--highlighted {
        border: 1px solid theme-color-hex('primary');
        background-color: $hoverBackgroundColor;

        &:hover {
            border: 1px solid theme-color-hex('primary');
        }
    }

    &.gst-add-hotel-reservation-hotel-list-card--no-radius-top {
        border-radius: 0 0 border-radius('xs') border-radius('xs');

        @include mobile-only {
            border-radius: 0;
        }
    }

    .content-wrapper {
        display: flex;
        flex-grow: 1;
    }

    .v-image {
        border-radius: 2px;
    }

    .gst-add-hotel-reservation-hotel-list-card__rating {
        height: theme-spacing(5);
        margin-top: 2px;
        float: left;
    }

    .gst-add-hotel-reservation-hotel-list-card__distance {
        @include font-size('xxs');
        @include line-height('m');

        color: theme-color('tertiary');
    }

    .gst-add-hotel-reservation-hotel-list-card__name {
        @include font-size('l');
        @include font-weight('large');
        @include line-height('xxxl');

        color: theme-color('quaternary');
    }

    .gst-add-hotel-reservation-hotel-list-card__room {
        @include font-size('s');
        @include line-height('xxxl');

        position: relative;
    }

    .gst-add-hotel-reservation-hotel-list-card__gifts {
        gap: theme-spacing(1);
    }

    .gst-add-hotel-reservation-hotel-list-card__icon {
        path {
            fill: theme-color('tertiary');
        }
    }

    .gst-add-hotel-reservation-hotel-list-card__icon:hover {
        path {
            fill: theme-color('primary');
        }
    }
}

@include mobile-only {
    .gst-add-hotel-reservation-hotel-list-card {
        border: unset;
        border-bottom: 1px solid theme-color-hex('senary-darken-2');
        border-radius: border-radius('none');

        &:hover {
            border: unset;
            background-color: #ebf5ff;
            border-bottom: 1px solid #ebf5ff;
        }

        .gst-add-hotel-reservation-hotel-list-card__distance {
            margin-top: theme-spacing(1);
        }

        .gst-add-hotel-reservation-hotel-list-card__rating-wrapper
            .gst-add-hotel-reservation-hotel-list-card__distance {
            position: relative;
            bottom: theme-spacing(1);
            margin-top: 0;
        }

        .gst-add-hotel-reservation-hotel-list-card__room {
            top: 2px;
        }

        .gst-add-hotel-reservation-hotel-list-card__rating {
            position: relative;
            bottom: theme-spacing(1);
        }
    }
}
</style>
